'use client'

import * as React from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

// `.icon-btn` from the prototype theme.css: round flex-center button with a
// springy press (scale 0.92, spatial easing on transform). We transition only
// `transform`, not the prototype's `background`: our token backgrounds are
// `rgb(var(--token))`, and this Chromium won't interpolate a bg transition
// across a CSS variable — a state-driven bg swap (e.g. the AI-summary icon's
// idle→open tint) sticks at the idle color instead. Letting the background
// swap instantly is correct and matches the pill/other token-bg controls.
export const iconBtnBase =
  'flex shrink-0 items-center justify-center rounded-full ' +
  '[transition:transform_0.15s_cubic-bezier(0.3,1.3,0.4,1)] ' +
  'active:scale-[0.92]'

export interface IconBtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Diameter in px. */
  size?: number
}

export const IconBtn = React.forwardRef<HTMLButtonElement, IconBtnProps>(
  ({ size = 36, className, style, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(iconBtnBase, className)}
      style={{ width: size, height: size, ...style }}
      {...props}
    />
  )
)
IconBtn.displayName = 'IconBtn'

// Circular button that sits on a photo (scrim background) — ui.jsx:84-96.
// Active state fills the icon with the primary color; otherwise white.
export interface ScrimBtnProps {
  icon: LucideIcon
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  active?: boolean
  title?: string
  size?: number
  className?: string
  style?: React.CSSProperties
}

export function ScrimBtn({ icon: Icon, onClick, active, title, size = 36, className, style }: ScrimBtnProps) {
  return (
    <IconBtn
      size={size}
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
      className={cn('bg-[rgba(26,23,23,0.5)]', active ? 'text-primary' : 'text-white', className)}
      style={style}
    >
      <Icon size={Math.round(size * 0.52)} fill={active ? 'currentColor' : 'none'} />
    </IconBtn>
  )
}
